import { motion } from 'motion/react';
import { TrendingUp, Users, Briefcase, ArrowRight } from 'lucide-react';

const flavors = [
  {
    id: 'sales',
    icon: TrendingUp,
    title: 'xRepl.ai for Sales',
    description: 'Close deals faster with AI-powered outreach that sounds human, builds rapport, and converts prospects.',
    benefits: [
      'Personalized cold outreach at scale',
      'Follow-up sequences that convert',
      'Handle objections with confidence',
      'Maintain your authentic voice'
    ],
    highlighted: true
  },
  {
    id: 'recruiters',
    icon: Users,
    title: 'xRepl.ai for Recruiters',
    description: 'Source top talent with compelling, personalized messages that candidates actually want to respond to.',
    benefits: [
      'Engage passive candidates effectively',
      'Craft compelling job descriptions',
      'Streamline interview coordination',
      'Build authentic relationships'
    ],
    highlighted: false
  },
  {
    id: 'jobseekers',
    icon: Briefcase,
    title: 'xRepl.ai for Job Seekers',
    description: 'Land your dream job with professional networking messages and interview follow-ups that stand out.',
    benefits: [
      'Professional networking messages',
      'Thoughtful interview thank-yous',
      'Compelling cover letter assistance',
      'Confident salary negotiations'
    ],
    highlighted: false
  }
];

export function Flavors() {
  return (
    <section id="roles" className="py-20 bg-white/80 dark:bg-[#0A1628]/60 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Built for Your Role
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Specialized AI training for your specific communication needs
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {flavors.map((flavor, index) => (
            <motion.div
              key={flavor.title}
              id={flavor.id}
              className={`group p-8 rounded-2xl border-2 transition-all cursor-pointer ${
                flavor.highlighted
                  ? 'bg-gradient-to-br from-[#1E3A8A] to-[#60A5FA] border-[#60A5FA] text-white'
                  : 'bg-gray-50 dark:bg-[#1E3A8A]/10 border-gray-200 dark:border-[#1E3A8A]/30 hover:border-[#60A5FA] dark:hover:border-[#60A5FA]'
              } hover:shadow-2xl hover:shadow-[#60A5FA]/20`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -8 }}
            >
              <div
                className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform ${
                  flavor.highlighted
                    ? 'bg-white/20'
                    : 'bg-[#1E3A8A] dark:bg-[#60A5FA]'
                }`}
              >
                <flavor.icon
                  className={flavor.highlighted ? 'text-white' : 'text-white'}
                  size={28}
                />
              </div>

              <h3
                className={`text-2xl font-bold mb-3 ${
                  flavor.highlighted ? 'text-white' : 'text-gray-900 dark:text-white'
                }`}
              >
                {flavor.title}
              </h3>

              <p
                className={`mb-6 ${
                  flavor.highlighted
                    ? 'text-white/90'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {flavor.description}
              </p>

              <ul className="space-y-3 mb-8">
                {flavor.benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-2">
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        flavor.highlighted
                          ? 'bg-white/20'
                          : 'bg-[#60A5FA]/20'
                      }`}
                    >
                      <div
                        className={`w-2 h-2 rounded-full ${
                          flavor.highlighted ? 'bg-white' : 'bg-[#60A5FA]'
                        }`}
                      />
                    </div>
                    <span
                      className={
                        flavor.highlighted
                          ? 'text-white/90'
                          : 'text-gray-700 dark:text-gray-300'
                      }
                    >
                      {benefit}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                className={`w-full py-3 rounded-lg flex items-center justify-center gap-2 transition-all group ${
                  flavor.highlighted
                    ? 'bg-white text-[#1E3A8A] hover:bg-white/90'
                    : 'bg-[#1E3A8A] dark:bg-[#60A5FA] text-white hover:bg-[#1E3A8A]/90 dark:hover:bg-[#60A5FA]/90'
                }`}
              >
                Learn More
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}